import React, { useRef, useState, useEffect } from 'react'
import { DeleteIcon } from '../../Icons/DeleteIcon'
import { PenIcon } from '../../Icons/PenIcon'
import { t } from 'i18next'
import { useDNDList } from '../../../utils/hooks/useDNDList'
import { BiGlobe } from 'react-icons/bi'
import {
  AiFillEye,
  AiFillEyeInvisible,
  AiOutlineCalendar,
  AiOutlineUser,
} from 'react-icons/ai'
import { StyledContextMenu } from '../../ContextMenu/StyledContextMenu'
import { ContextMenuTrigger } from 'react-contextmenu'
import { StyledItemWrapperDND } from '../../TreeList/dndTreeList'
import MediaQuery from 'react-responsive'
import MoreOptionsButton from '../../Buttons/MoreOptionsButton'
import PortalContextMenu from '../../ContextMenu/PortalContextMenu'
import { formatedDate } from '../../../utils/formatedDate'
import styled from 'styled-components'
import apiClient from '../../../utils/api/apiClient'
import { APIEndpoints } from '../../../utils/api/apiConfig'

const ColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 6px 10px;
  cursor: pointer;
  user-select: none;
`

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
`

const AuthorText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  font-weight: 600;
  color: #4b5563;
  letter-spacing: 0.01em;
`

const DateBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  padding: 1px 8px;
`

const EyeIcon = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ visible }) => (visible ? '#16a34a' : '#ef4444')};
  cursor: default;
  flex-shrink: 0;

  &:hover > span {
    opacity: 1;
    transform: translateX(-50%) translateY(-2px);
  }
`

const Tooltip = styled.span`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%) translateY(0);
  background: #1f2937;
  color: #fff;
  font-size: 10.5px;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 5px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s, transform 0.15s;
  z-index: 100;
`

const SkeletonEye = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;
  flex-shrink: 0;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`

const TitleText = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  line-height: 1.4;
  letter-spacing: -0.1px;
`

const ArrowButton = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: transparent;
  transition: background 0.18s;

  svg {
    transition: transform 0.24s cubic-bezier(0.4, 0, 0.2, 1);
    transform: ${({ expanded }) =>
      expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`

const BodyPanel = styled.div`
  max-height: ${({ expanded }) => (expanded ? '1200px' : '0px')};
  overflow: hidden;
  transition: max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
`

const BodyContent = styled.div`
  padding: 14px 6px 10px;
  border-top: 1px solid #f0f0f0;
  font-size: 13.5px;
  line-height: 1.78;
  color: #374151;
  cursor: default;

  p {
    margin-bottom: 0.5em;
  }
  ul,
  ol {
    padding-left: 1.4em;
    margin-bottom: 0.5em;
  }
  img {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    margin: 8px 0;
    display: block;
  }
  strong,
  b {
    font-weight: 600;
  }
  a {
    color: #2563eb;
  }
`
const NewsItem = ({
  news,
  setDeleteNewsId,
  handleEditNews,
  index,
  moveCardHandler,
  isMainMandator,
  setEditNewsIdAssignments,
  refreshToken,
}) => {
  const ref = useRef(null)
  const [expanded, setExpanded] = useState(false)
  const [details, setDetails] = useState(null)
  const [fetching, setFetching] = useState(false)
  const [fetchError, setFetchError] = useState(false)

  const { itemPosition, isOver, isDragging, drag, drop } = useDNDList(
    index,
    moveCardHandler,
    'CARD'
  )
  const opacity = isDragging ? '0.2' : '1'
  drag(drop(ref))

  useEffect(() => {
    let cancelled = false
    const fetchDetails = async () => {
      setFetching(true)
      try {
        const res = await apiClient.getJson(
          APIEndpoints.feedsAdministration(news.id).edit
        )
        if (!cancelled) setDetails(res.news)
      } catch (e) {
        if (!cancelled) setFetchError(true)
      } finally {
        if (!cancelled) setFetching(false)
      }
    }
    fetchDetails()
    return () => {
      cancelled = true
    }
  }, [news.id, refreshToken])

  const renderEyeIcon = () => {
    if (fetching) return <SkeletonEye />
    if (!details || fetchError) return null

    const visible = details.isVisibleNews
    return (
      <EyeIcon visible={visible} onClick={(e) => e.stopPropagation()}>
        {visible ? <AiFillEye size={17} /> : <AiFillEyeInvisible size={17} />}
        <Tooltip>
          {visible ? t('modal:msgVisible') : t('newsAdmin:msgNotVisible')}
        </Tooltip>
      </EyeIcon>
    )
  }

  const options = [
    {
      option: 'edit',
      optionText: t('edit'),
      optionIcon: <PenIcon />,
      onOptionSelect: () => handleEditNews(news.id),
    },
    {
      option: 'delete',
      optionText: t('deleteButton'),
      optionIcon: <DeleteIcon />,
      onOptionSelect: () => setDeleteNewsId(news.id),
    },
    isMainMandator
      ? {
          option: 'assignNews',
          optionText: t('newsAdmin:assignNews'),
          optionIcon: <BiGlobe />,
          onOptionSelect: () => setEditNewsIdAssignments(news.id),
        }
      : null,
  ]

  return (
    <StyledContextMenu>
      <ContextMenuTrigger id={`${news.id}_context`} holdToDisplay={-1}>
        <StyledItemWrapperDND
          ref={ref}
          id={`${index}_id`}
          style={{ opacity }}
          showTopLine={itemPosition === 'above' && isOver}
          showBottomLine={itemPosition === 'below' && isOver}
          hierarchyLevel={0}
          isClickable
        >
          <ColumnWrapper>
            <HeaderRow onClick={() => setExpanded((v) => !v)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <MetaRow>
                  {news.createdByDisplayName && (
                    <AuthorText>
                      <AiOutlineUser size={13} color='#6b7280' />
                      {news.createdByDisplayName}
                    </AuthorText>
                  )}
                  <DateBadge>
                    <AiOutlineCalendar size={12} color='#9ca3af' />
                    {formatedDate(news.createdAt)}
                  </DateBadge>
                  {renderEyeIcon()}
                </MetaRow>

                <TitleText>
                  {news.header ? news.header : t('newsAdmin:noHeadline')}
                </TitleText>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexShrink: 0,
                }}
              >
                <ArrowButton expanded={expanded}>
                  <svg width='14' height='14' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M6 9l6 6 6-6'
                      stroke={expanded ? '#1D4ED8' : '#6B7280'}
                      strokeWidth='2.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </ArrowButton>

                <MediaQuery maxWidth={1499}>
                  <div onClick={(e) => e.stopPropagation()}>
                    <MoreOptionsButton options={options} />
                  </div>
                </MediaQuery>
              </div>
            </HeaderRow>

            <BodyPanel expanded={expanded}>
              <BodyContent
                onClick={(e) => e.stopPropagation()}
                dangerouslySetInnerHTML={{ __html: news.body || '—' }}
              />
            </BodyPanel>
          </ColumnWrapper>
        </StyledItemWrapperDND>
      </ContextMenuTrigger>
      <PortalContextMenu contextId={`${news.id}_context`} options={options} />
    </StyledContextMenu>
  )
}

export default NewsItem
